import React, { useState, useEffect } from 'react';
import { Text, BlockStack, Box, Badge, Banner, InlineStack } from '@shopify/polaris';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { patchRequest } from '../../../shared/components/functions';
import InteractionCard from './InteractionCard';

export default function InteractionKanban({ statuses, interactions, onInteractionClick, onInteractionsChange }) {
  const [localInteractions, setLocalInteractions] = useState(interactions);

  useEffect(() => {
    setLocalInteractions(interactions);
  }, [interactions]);

  // Filter to only kanban-visible statuses
  const kanbanStatuses = statuses.filter((s) => s.is_kanban_visible);

  // Group interactions by status and sort by kanban_order
  const getInteractionsByStatus = (statusId) => {
    return localInteractions.filter((i) => i.interaction_status_id === statusId).sort((a, b) => (a.kanban_order || 0) - (b.kanban_order || 0));
  };

  // Get interactions with no status (null), sorted by kanban_order
  const unassignedInteractions = localInteractions.filter((i) => !i.interaction_status_id).sort((a, b) => (a.kanban_order || 0) - (b.kanban_order || 0));

  const ORDER_GAP = 1000; // Gap between items for ordering

  const handleCardStatusChange = (interactionId, newStatusId) => {
    // Update local state immediately
    setLocalInteractions((prev) => prev.map((i) => (i.id === interactionId ? { ...i, interaction_status_id: newStatusId } : i)));
    // Trigger a refresh from parent
    onInteractionsChange?.();
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    // If dropped in the same place, do nothing
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const interactionId = parseInt(draggableId);
    const newStatusId = destination.droppableId === 'unassigned' ? null : parseInt(destination.droppableId);
    const isSameColumn = source.droppableId === destination.droppableId;

    // Get the items in the destination column (excluding the dragged item)
    const destStatusId = destination.droppableId === 'unassigned' ? null : parseInt(destination.droppableId);
    const destItems = localInteractions.filter((i) => i.interaction_status_id === destStatusId && i.id !== interactionId).sort((a, b) => (a.kanban_order || 0) - (b.kanban_order || 0));

    // Calculate new kanban_order
    let newOrder;
    if (destItems.length === 0) {
      // First item in column
      newOrder = 0;
    } else if (destination.index === 0) {
      // Moving to the top
      newOrder = (destItems[0]?.kanban_order || 0) - ORDER_GAP;
    } else if (destination.index >= destItems.length) {
      // Moving to the bottom
      newOrder = (destItems[destItems.length - 1]?.kanban_order || 0) + ORDER_GAP;
    } else {
      // Moving between two items
      const prevOrder = destItems[destination.index - 1]?.kanban_order || 0;
      const nextOrder = destItems[destination.index]?.kanban_order || 0;
      newOrder = prevOrder + (nextOrder - prevOrder) / 2;
    }

    // Optimistic update
    setLocalInteractions((prev) => prev.map((i) => (i.id === interactionId ? { ...i, interaction_status_id: newStatusId, kanban_order: newOrder } : i)));

    try {
      await patchRequest(`/api/interactions/${interactionId}/status`, {
        interaction_status_id: newStatusId,
        kanban_order: newOrder
      });
      onInteractionsChange?.();
    } catch (error) {
      console.error('Error updating interaction status:', error);
      // Revert on error
      setLocalInteractions(interactions);
    }
  };

  if (kanbanStatuses.length === 0) {
    return (
      <Box padding="400">
        <Banner tone="info">
          <Text as="p">No statuses are marked as "Show in Kanban". Go to "Manage Statuses" to enable statuses for the Kanban view.</Text>
        </Banner>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          overflowX: 'auto',
          padding: '0.5rem 0',
          minHeight: '500px'
        }}
      >
        {/* Unassigned column - only show if there are unassigned interactions */}
        {unassignedInteractions.length > 0 && (
          <div style={{ minWidth: '260px', maxWidth: '260px' }}>
            <div style={{ background: 'var(--p-color-bg-surface)', borderRadius: '12px', border: '1px solid var(--p-color-border)', padding: '8px' }}>
              <div style={{ marginBottom: '8px' }}>
                <InlineStack gap="100" blockAlign="center">
                  <Badge tone="default">No Status</Badge>
                  <Text as="span" tone="subdued" variant="bodySm">
                    ({unassignedInteractions.length})
                  </Text>
                </InlineStack>
              </div>
              <Droppable droppableId="unassigned">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      minHeight: '100px',
                      padding: '4px',
                      backgroundColor: snapshot.isDraggingOver ? 'var(--p-color-bg-surface-hover)' : 'transparent',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <BlockStack gap="100">
                      {unassignedInteractions.map((interaction, index) => (
                        <Draggable key={interaction.id} draggableId={String(interaction.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.9 : 1,
                                zIndex: snapshot.isDragging ? 9999 : 'auto'
                              }}
                            >
                              <InteractionCard interaction={interaction} statuses={statuses} onClick={() => onInteractionClick?.(interaction)} onStatusChange={handleCardStatusChange} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                    </BlockStack>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        )}

        {/* Status columns */}
        {kanbanStatuses.map((status) => {
          const statusInteractions = getInteractionsByStatus(status.id);

          return (
            <div key={status.id} style={{ minWidth: '260px', maxWidth: '260px' }}>
              <div style={{ background: 'var(--p-color-bg-surface)', borderRadius: '12px', border: '1px solid var(--p-color-border)', padding: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <InlineStack gap="100" blockAlign="center">
                    <Badge tone={status.color}>{status.name}</Badge>
                    <Text as="span" tone="subdued" variant="bodySm">
                      ({statusInteractions.length})
                    </Text>
                  </InlineStack>
                </div>
                <Droppable droppableId={String(status.id)}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        minHeight: '100px',
                        padding: '4px',
                        backgroundColor: snapshot.isDraggingOver ? 'var(--p-color-bg-surface-hover)' : 'transparent',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <BlockStack gap="100">
                        {statusInteractions.map((interaction, index) => (
                          <Draggable key={interaction.id} draggableId={String(interaction.id)} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.9 : 1,
                                  zIndex: snapshot.isDragging ? 9999 : 'auto'
                                }}
                              >
                                <InteractionCard interaction={interaction} statuses={statuses} onClick={() => onInteractionClick?.(interaction)} onStatusChange={handleCardStatusChange} />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </BlockStack>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
